import {
	isConcludedStatus,
	isSettledStatus,
	isSignedStatus,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/Documents.utils';
import { canClaimSigner } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignersTab.claim.utils';
import { GroupMenuItem } from '@dealstack/frontend/shared/components/ExpandablePersonCard/GroupHeader.types';
import type { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import type {
	SignerWithRepresentative,
	SignerWithRepresentativeGroup,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/components/SignerTab.utils';
import {
	DocumentSigningRoleType,
	DocumentSigningScope,
	DocumentWorkflowInstanceSignatureStatus,
	InvestmentProgrammeDocumentDetailsSignerRes,
	SignatureType,
	TemplateSettingLockRes,
} from '@dealstack/shared/lib/generated';

export const getIsSignerReadyToSign = ({
	documentSignatureType,
	documentSigningScope,
	signer,
	isSignerUpdatePending,
	isEidEasyDocument,
}: {
	documentSignatureType?: SignatureType | null;
	documentSigningScope?: DocumentSigningScope | null;
	signer: InvestmentProgrammeDocumentDetailsSignerRes;
	isSignerUpdatePending: boolean;
	/** `eSignProvider === EID_EASY` for this document. */
	isEidEasyDocument?: boolean;
}) => {
	if (documentSignatureType === SignatureType.ELECTRONIC) {
		// eID Easy is the only electronic route there is. Every signer carries a
		// signatureId, so without this gate a legacy template with no provider recorded
		// would offer a Sign button whose only outcome is the "no signing URL" toast.
		return isEidEasyDocument === true && !!signer.signatureId && !isSignerUpdatePending;
	}

	if (documentSignatureType === SignatureType.WET_INK) {
		if (documentSigningScope === DocumentSigningScope.ALL_PARTICIPANTS) {
			return !!signer.fileId && !isSignerUpdatePending;
		}
	}

	return true;
};

/** Wet ink has no witness signers — witnessing is a printed field on the signature page. */
export const witnessSignersSupported = (documentSignatureType?: SignatureType | null): boolean =>
	documentSignatureType !== SignatureType.WET_INK;

interface WitnessControlsInput {
	signer: {
		signatureId?: string;
		status?: DocumentWorkflowInstanceSignatureStatus;
		witnesses?: { status?: DocumentWorkflowInstanceSignatureStatus }[];
		requiresWitness?: boolean;
	};
	groupType: DocumentSigningRoleType;
	documentSignatureType?: SignatureType | null;
	onInviteWitness?: (parentSignatureId: string) => void;
}

export interface WitnessControlsResult {
	showWitnessButton: boolean;
	hasExistingWitness: boolean;
}

export const getWitnessControlsForSigner = ({
	signer,
	groupType,
	documentSignatureType,
	onInviteWitness,
}: WitnessControlsInput): WitnessControlsResult => {
	const isWitnessGroup = groupType === DocumentSigningRoleType.WITNESS;
	const signerHasSigned = isSignedStatus(signer.status);
	const witnesses = signer.witnesses ?? [];
	const hasExistingWitness = witnesses.length > 0;
	// Concluded rather than signed: DC-1098 closes an outstanding witness slot, and there is
	// no one left to invite into a closed document.
	const allWitnessesConcluded =
		hasExistingWitness && witnesses.every((w) => isConcludedStatus(w.status));

	const showWitnessButton =
		witnessSignersSupported(documentSignatureType) &&
		!isWitnessGroup &&
		signerHasSigned &&
		!allWitnessesConcluded &&
		!!signer.requiresWitness &&
		!!signer.signatureId &&
		!!onInviteWitness;

	return { showWitnessButton, hasExistingWitness };
};

/**
 * Every signer rendered inside a group, including the PIV / Representative signers that are
 * folded into each party card as nested children. The group-header aggregate state (and its
 * enable/disable direction) must reflect all of them, not just the top-level PARTY signers —
 * otherwise a group-wide toggle looks like it "didn't apply to everyone".
 */
export const flattenGroupSigners = (
	signers: SignerWithRepresentative[]
): InvestmentProgrammeDocumentDetailsSignerRes[] =>
	signers.flatMap((signer) => [
		signer,
		...(signer.piv ? [signer.piv] : []),
		...(signer.representative ? [signer.representative] : []),
	]);

/**
 * The distinct signing-role ids present in a group: the group's own role plus the roles of any
 * nested PIV / Representative signers. A group-wide toggle fans out across all of these so the
 * override lands on every signer shown in the group, not only the PARTY role.
 */
export const collectGroupRoleIds = (group: SignerWithRepresentativeGroup): string[] => {
	const roleIds = new Set<string>();
	if (group.roleId) {
		roleIds.add(group.roleId);
	}
	for (const signer of group.signers) {
		if (signer.pivRoleId) {
			roleIds.add(signer.pivRoleId);
		}
		if (signer.representativeRoleId) {
			roleIds.add(signer.representativeRoleId);
		}
	}
	return [...roleIds];
};

/**
 * Availability rules for the simple-sign / witness toggles, shared between the
 * group-header (by-role, aggregate scope) control and the per-signer card menu.
 * Callers supply the scope-appropriate `pending` / `fullyComplete` /
 * `enablingWitnessBlocked` inputs (group aggregate vs single signer).
 */
export const canToggleSimpleSign = ({
	isDealstackSupport,
	isWitnessGroup,
	eSignInitiated,
	pending,
	fullyComplete,
}: {
	/** Simple sign is configurable by DealStack Support only; the API refuses anyone else. */
	isDealstackSupport: boolean;
	isWitnessGroup: boolean;
	eSignInitiated: boolean;
	pending: boolean;
	fullyComplete: boolean;
}): boolean =>
	isDealstackSupport && !isWitnessGroup && !eSignInitiated && !pending && !fullyComplete;

export const canToggleWitness = ({
	isWitnessGroup,
	eSignInitiated,
	pending,
	fullyComplete,
	enablingWitnessBlocked,
	witnessSupported,
	witnessRequired,
}: {
	isWitnessGroup: boolean;
	eSignInitiated: boolean;
	pending: boolean;
	fullyComplete: boolean;
	// Enabling a witness after the signer(s) have already signed is blocked; disabling
	// stays available so an admin can unblock the document.
	enablingWitnessBlocked: boolean;
	/** False for wet ink, which has no witness signers to require. */
	witnessSupported: boolean;
	/** Witnessing is currently on, so the toggle would turn it off. */
	witnessRequired: boolean;
}): boolean =>
	// Like enablingWitnessBlocked, wet ink only blocks enabling — a legacy signer that already
	// requires a witness stays clearable.
	(witnessSupported || witnessRequired) &&
	!isWitnessGroup &&
	!eSignInitiated &&
	!pending &&
	!fullyComplete &&
	!enablingWitnessBlocked;

/**
 * Whether ENABLING a witness requirement is still possible, per the API.
 *
 * Document-scoped, not signer-scoped: the pages are laid out once for the whole document. Cannot
 * be derived client-side — `eSignInitiated` is per-session and resets on reload. Already required
 * is not a change, which is what keeps a re-save working.
 */
export const isEnablingWitnessBlocked = ({
	currentlyRequired,
	witnessRequirementLock,
}: {
	currentlyRequired: boolean;
	witnessRequirementLock?: TemplateSettingLockRes;
}): boolean => !currentlyRequired && !!witnessRequirementLock?.locked;

interface SignerMenuItemsInput {
	/** The individual signer the toggles apply to. Omitted → no menu. */
	signerId?: string;
	isDealstackSupport: boolean;
	/** Resolved: signer.effectiveSecurityLevel === BASIC. */
	simpleSignAllowed: boolean;
	/** Resolved: signer.requiresWitness. */
	witnessRequired: boolean;
	signerStatus?: DocumentWorkflowInstanceSignatureStatus;
	/**
	 * The whole document workflow is COMPLETED. Once true the toggles are moot and
	 * hidden regardless of the individual signer's status — an unsigned optional slot
	 * (e.g. a 2-of-3 counterparty role) would otherwise never reach COMPLETED itself.
	 */
	documentFullySigned: boolean;
	isWitnessGroup: boolean;
	documentSignatureType?: SignatureType | null;
	eSignInitiated: boolean;
	securityLevelPending: boolean;
	witnessPending: boolean;
	/** The document's lock on enabling a witness requirement, from the details read. */
	witnessRequirementLock?: TemplateSettingLockRes;
	onToggleSimpleSign: () => void;
	onToggleWitness: () => void;
	/** The "Remove from document" entry, already gated by `canRemoveSigner`. Appended last. */
	removeItem?: GroupMenuItem;
	labels: {
		enableSimpleSign: string;
		disableSimpleSign: string;
		enableWitnessRequirement: string;
		disableWitnessRequirement: string;
	};
}

export const buildSignerMenuItems = ({
	signerId,
	isDealstackSupport,
	simpleSignAllowed,
	witnessRequired,
	signerStatus,
	documentFullySigned,
	isWitnessGroup,
	documentSignatureType,
	eSignInitiated,
	securityLevelPending,
	witnessPending,
	witnessRequirementLock,
	onToggleSimpleSign,
	onToggleWitness,
	removeItem,
	labels,
}: SignerMenuItemsInput): GroupMenuItem[] => {
	const items: GroupMenuItem[] = [];

	if (!signerId) {
		return items;
	}

	// Settled, not strictly COMPLETED: a finished signature rests at SIGNED, so
	// gating on COMPLETED alone left both toggles offered on a document with nothing left to do.
	const isFullyComplete = documentFullySigned || isSettledStatus(signerStatus);

	if (
		canToggleSimpleSign({
			isDealstackSupport,
			isWitnessGroup,
			eSignInitiated,
			pending: securityLevelPending,
			fullyComplete: isFullyComplete,
		})
	) {
		items.push({
			key: 'toggle-simple-sign',
			label: simpleSignAllowed ? labels.disableSimpleSign : labels.enableSimpleSign,
			onClick: onToggleSimpleSign,
			testId: TEST_ELEMENT_IDS.DOCUMENT_SIGNER_TOGGLE_SIMPLE_SIGN,
		});
	}

	if (
		canToggleWitness({
			isWitnessGroup,
			eSignInitiated,
			pending: witnessPending,
			fullyComplete: isFullyComplete,
			enablingWitnessBlocked: isEnablingWitnessBlocked({
				currentlyRequired: witnessRequired,
				witnessRequirementLock,
			}),
			witnessSupported: witnessSignersSupported(documentSignatureType),
			witnessRequired,
		})
	) {
		items.push({
			key: 'toggle-witness-requirement',
			label: witnessRequired
				? labels.disableWitnessRequirement
				: labels.enableWitnessRequirement,
			onClick: onToggleWitness,
			testId: TEST_ELEMENT_IDS.DOCUMENT_SIGNER_TOGGLE_WITNESS,
		});
	}

	if (removeItem) {
		items.push(removeItem);
	}

	return items;
};

/**
 * An invited authorised signatory can be cancelled while nothing has been committed —
 * a signed or closed slot is terminal. Mirrors the backend guard.
 */
export const canCancelAuthorisedSignatory = ({
	isInvitedAuthorisedSignatory,
	signatureId,
	status,
}: {
	isInvitedAuthorisedSignatory: boolean;
	signatureId?: string;
	status?: DocumentWorkflowInstanceSignatureStatus;
}): boolean => isInvitedAuthorisedSignatory && !!signatureId && !isConcludedStatus(status);

export const getDisplaySignerStatus = (
	signer: {
		status?: DocumentWorkflowInstanceSignatureStatus;
		witnesses?: { status?: DocumentWorkflowInstanceSignatureStatus }[];
	},
	witnessActionAvailable: boolean
): DocumentWorkflowInstanceSignatureStatus | undefined => {
	if (signer.status !== DocumentWorkflowInstanceSignatureStatus.SIGNED) {
		return signer.status;
	}

	const witnesses = signer.witnesses ?? [];
	const hasUnsignedWitness = witnesses.some((w) => !isSignedStatus(w.status));

	if (hasUnsignedWitness || witnessActionAvailable) {
		return DocumentWorkflowInstanceSignatureStatus.WAITING_FOR_WITNESS_SIGNATURES;
	}

	return signer.status;
};

/**
 * A signer can be claimed & signed on behalf of only if it is backed by an
 * investor (PARTY/REPRESENTATIVE/PIV all carry `investorId`; counterparties do
 * not) and the role-agnostic `canClaimSigner` gate passes (unsigned,
 * unclaimed, has an id). This is the single axis the UI gates the claim ⋯ menu
 * on — REP/PIV signers render as nested child cards, not as their own group
 * rows, so `groupType` is the wrong axis.
 */
export const isClaimableInvestorSigner = (
	signer: Pick<
		InvestmentProgrammeDocumentDetailsSignerRes,
		'id' | 'status' | 'claimedByName' | 'investorId'
	>
): boolean => !!signer.investorId && canClaimSigner(signer);

/**
 * True when this signer is currently claimed by the given user AND still owes a signature —
 * i.e. the current user has something to sign on its behalf. Used to decide whether the
 * claimed "Sign" button should be shown to this user. A slot closed without a signature
 * (DC-1098) owes nothing, so it drops the button along with the signed ones.
 */
export const isClaimedByCurrentUser = (
	signer: Pick<InvestmentProgrammeDocumentDetailsSignerRes, 'status' | 'claimedByUserId'>,
	currentUserId: string | undefined
): boolean =>
	!!signer.claimedByUserId &&
	signer.claimedByUserId === currentUserId &&
	!isConcludedStatus(signer.status);

export interface ClaimedSignGate {
	/** Claimed by the current user but its document hasn't been generated yet. */
	notYetGenerated: boolean;
	/** "Preparing to sign…" message to show while disabled, else undefined. */
	disabledMessage: string | undefined;
}

/**
 * A signature claimed on behalf isn't signable until the backend finishes
 * generating its document (the signature leaves `CREATED`). Resolves the
 * disabled state + "preparing" message for a claimed signer — shared by the
 * party signer and its PIV / representative cards.
 */
export const getClaimedSignGate = (
	claimedByUser: boolean,
	status: DocumentWorkflowInstanceSignatureStatus | undefined,
	tMipManage: TranslateFunction<'mip-manage'>
): ClaimedSignGate => {
	const notYetGenerated =
		claimedByUser && status === DocumentWorkflowInstanceSignatureStatus.CREATED;
	return {
		notYetGenerated,
		disabledMessage: notYetGenerated
			? tMipManage('documents_page.signer.preparing_to_sign')
			: undefined,
	};
};

export interface SignerActionGates {
	showSignButton: boolean;
	showResendButton: boolean;
}

/**
 * Sign / Resend visibility for one rendered signer.
 *
 * Both gate on the slot being *concluded* rather than *signed*: DC-1098 terminates every
 * outstanding slot at CLOSED_WITHOUT_SIGNATURE, which is deliberately not a signed status, so
 * asking "has this signer signed?" left a closed document still offering its counterparty a
 * Sign button and its invited signatory a Resend.
 */
export const getSignerActionGates = ({
	isCounterpartyGroup,
	isCurrentUserSigner,
	isSignerReadyToSign,
	hasSignHandler,
	status,
	parentClaimedByUser,
	hasClaimedSignHandler,
	isInvitedAuthorisedSignatory,
	signatureId,
	hasResendHandler,
}: {
	isCounterpartyGroup: boolean;
	isCurrentUserSigner: boolean;
	isSignerReadyToSign: boolean;
	hasSignHandler: boolean;
	status: DocumentWorkflowInstanceSignatureStatus | undefined;
	parentClaimedByUser: boolean;
	hasClaimedSignHandler: boolean;
	isInvitedAuthorisedSignatory: boolean;
	signatureId: string | undefined;
	hasResendHandler: boolean;
}): SignerActionGates => {
	const concluded = isConcludedStatus(status);

	return {
		showSignButton:
			(isCounterpartyGroup &&
				isCurrentUserSigner &&
				isSignerReadyToSign &&
				hasSignHandler &&
				!concluded) ||
			(parentClaimedByUser && hasClaimedSignHandler),
		showResendButton:
			isInvitedAuthorisedSignatory && !concluded && !!signatureId && hasResendHandler,
	};
};
